/**
 * CommandPalette — globalny Ctrl+K command palette (Kompas-style).
 *
 * 2026-05-26 (B-track z audytu):
 * - Listener globalny na Ctrl+K / Cmd+K.
 * - 3 kategorie:
 *   1. NAWIGACJA — wszystkie strony z routera (z RBAC gate per handlowiec).
 *   2. WYSZUKIWANIE — query tenders/investments (deferred — pisze gdy >=2 znaki).
 *   3. AKCJE — invite user, restart tutorial, toggle sidebar simple, glossary, settings.
 *
 * Wpięte w App.js na poziomie root. localStorage `__maskil_ctrlk_seen` jako hint dla
 * onboardingu (TODO).
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandSeparator, CommandShortcut,
} from '../ui/command';
import { toast } from 'sonner';
import {
  FileText, Users, Building2,
  BarChart3, BookOpen, Bug, Settings,
  Mail, Compass, MessageSquare, Layers,
  Brain, Zap, AlertCircle, RotateCcw, Eye, Network,
} from 'lucide-react';
import { listTenders, listInvestments } from '../../lib/api';
import { restartTutorial } from '../../lib/tutorialActions';

// ============================================================
// Static items — NAWIGACJA (z RBAC-aware filtering)
// ============================================================

const NAV_ITEMS_ALL = [
  // Codzienne (essential)
  { key: 'my-dashboard', icon: BarChart3, label: 'Mój dashboard', path: '/my-dashboard', tag: 'codzienne' },
  { key: 'feed', icon: Zap, label: 'Activity Feed', path: '/feed', tag: 'codzienne' },
  { key: 'tenders', icon: FileText, label: 'Przetargi (BZP/TED/Licytacje)', path: '/tenders', tag: 'leady' },
  { key: 'investments', icon: Building2, label: 'Inwestycje (GUNB)', path: '/investments', tag: 'leady' },
  { key: 'pre-tender', icon: Eye, label: 'Pre-Tender (sygnały wyprzedzające)', path: '/pre-tender', tag: 'leady' },
  { key: 'architects', icon: Users, label: 'Architekci', path: '/architects', tag: 'sieć' },
  { key: 'suppliers', icon: Users, label: 'Dostawcy', path: '/contractors', tag: 'sieć', adminOnly: true },
  { key: 'inbox', icon: Mail, label: 'Inbox (emaile)', path: '/inbox', tag: 'komunikacja', adminOnly: true },
  // Analiza
  { key: 'pipeline', icon: Layers, label: 'Pipeline (Kanban)', path: '/pipeline', tag: 'analiza', adminOnly: true },
  { key: 'negotiations', icon: MessageSquare, label: 'Negocjacje', path: '/negotiations', tag: 'analiza', adminOnly: true },
  { key: 'intelligence', icon: Network, label: 'Intelligence (SPHINX)', path: '/intelligence', tag: 'analiza', adminOnly: true },
  { key: 'project-analyzer', icon: Brain, label: 'Project Analyzer', path: '/project-analyzer', tag: 'analiza', adminOnly: true },
  // Konfiguracja
  { key: 'automations', icon: Zap, label: 'Reguły automatyzacji', path: '/automations', tag: 'konfiguracja', adminOnly: true },
  { key: 'agent-config', icon: Brain, label: 'Konfiguracja agenta', path: '/agent-config', tag: 'konfiguracja', adminOnly: true },
  { key: 'products', icon: Layers, label: 'Katalog produktów', path: '/products', tag: 'konfiguracja' },
  { key: 'settings', icon: Settings, label: 'Ustawienia', path: '/settings', tag: 'konfiguracja' },
  // Pomoc
  { key: 'glossary', icon: BookOpen, label: 'Słownik branżowy (SWZ, PZP, JEDZ…)', path: '/glossary', tag: 'pomoc' },
  { key: 'my-reports', icon: Bug, label: 'Moje zgłoszenia', path: '/zgloszenia', tag: 'pomoc' },
];

// ============================================================
// Static items — AKCJE (callbacks)
// ============================================================

function buildActions({ navigate, lang, user, onLogout }) {
  return [
    {
      key: 'action-glossary',
      icon: BookOpen,
      label: 'Otwórz słownik branżowy',
      hint: 'SWZ, PZP, JEDZ, CPV, UGR i 20+ innych',
      onSelect: () => navigate('/glossary'),
    },
    {
      key: 'action-toggle-sidebar-simple',
      icon: Compass,
      label: 'Przełącz Sidebar: Simple ⇄ Full',
      hint: 'Simple = tylko codzienne pozycje, Full = wszystkie 30+',
      onSelect: () => {
        const cur = localStorage.getItem('__maskil_sidebar_simple') === '1';
        localStorage.setItem('__maskil_sidebar_simple', cur ? '0' : '1');
        toast.success(`Sidebar: ${cur ? 'Full' : 'Simple'} — odśwież stronę`);
      },
    },
    {
      key: 'action-restart-tutorial',
      icon: RotateCcw,
      label: 'Pokaż samouczek od nowa',
      hint: 'Mission Board + Welcome modal',
      onSelect: async () => {
        if (!window.confirm('Zresetować postęp samouczka?')) return;
        try {
          const r = await restartTutorial();
          if (r.ok) {
            toast.success('Tutorial zresetowany — odśwież stronę');
            setTimeout(() => window.location.reload(), 1200);
          } else {
            toast.error(r.error || 'Błąd resetu');
          }
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Błąd resetu');
        }
      },
    },
    {
      key: 'action-report-bug',
      icon: Bug,
      label: 'Zgłoś problem techniczny',
      hint: 'Otwiera formularz z diagnostyką (URL, user_agent, JS errors)',
      onSelect: () => {
        // ReportIssueButton ma własny trigger w window, próbujemy go odpalić
        const btn = document.querySelector('[data-report-issue-trigger]');
        if (btn && typeof btn.click === 'function') {
          btn.click();
        } else {
          toast.info('Otwórz formularz przyciskiem 🐛 w prawym dolnym rogu');
        }
      },
    },
    user?.role === 'admin' && {
      key: 'action-settings-invite',
      icon: Users,
      label: 'Zaproś użytkownika do tenanta',
      hint: 'Settings → Użytkownicy → Zaproś',
      onSelect: () => navigate('/settings'),
    },
    onLogout && {
      key: 'action-logout',
      icon: AlertCircle,
      label: 'Wyloguj się',
      hint: 'Czyści sesję i wraca do logowania',
      onSelect: () => {
        if (window.confirm('Wylogować się?')) onLogout();
      },
    },
  ].filter(Boolean);
}

// ============================================================
// Component
// ============================================================

export default function CommandPalette({ user, onLogout, lang = 'pl' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ tenders: [], investments: [] });
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  // Global Ctrl+K / Cmd+K listener + custom event from TopBar Search button
  useEffect(() => {
    function onKey(e) {
      const isMac = navigator.platform.toLowerCase().includes('mac');
      const trigger = isMac
        ? (e.metaKey && e.key.toLowerCase() === 'k')
        : (e.ctrlKey && e.key.toLowerCase() === 'k');
      if (trigger) {
        e.preventDefault();
        setOpen((o) => !o);
      }
      // Esc handled by Dialog itself
    }
    function onCustomOpen() {
      setOpen(true);
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener('maskil:open-command-palette', onCustomOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('maskil:open-command-palette', onCustomOpen);
    };
  }, []);

  // Reset query when closed
  useEffect(() => {
    if (!open) {
      setQuery('');
      setSearchResults({ tenders: [], investments: [] });
    }
  }, [open]);

  // Debounced search (tenders + investments) — fires for queries ≥3 chars
  useEffect(() => {
    if (!open || query.trim().length < 3) {
      setSearchResults({ tenders: [], investments: [] });
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const [tendersRes, invsRes] = await Promise.all([
          listTenders({ search: query, limit: 5 }).catch(() => ({ items: [] })),
          listInvestments({ search: query, limit: 5 }).catch(() => ({ items: [] })),
        ]);
        if (!cancelled) {
          setSearchResults({
            tenders: tendersRes?.items || tendersRes?.opportunities || [],
            investments: invsRes?.items || invsRes?.opportunities || [],
          });
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, open]);

  // RBAC-filter nav items
  const navItems = useMemo(() => {
    return NAV_ITEMS_ALL.filter((item) => {
      if (item.adminOnly && user?.role === 'handlowiec') return false;
      return true;
    });
  }, [user]);

  const actions = useMemo(
    () => buildActions({ navigate, lang, user, onLogout }),
    [navigate, lang, user, onLogout]
  );

  const handleSelect = useCallback((onSelect) => {
    setOpen(false);
    // Defer so Dialog closes cleanly before navigate/state change
    setTimeout(() => onSelect && onSelect(), 50);
  }, []);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Szukaj stron, przetargów, inwestycji albo wykonaj akcję… (Ctrl+K)"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
          {searching ? 'Szukam…' : query.length < 3 && query.length > 0
            ? 'Wpisz co najmniej 3 znaki aby szukać przetargów/inwestycji.'
            : 'Brak wyników. Spróbuj innej frazy.'}
        </CommandEmpty>

        {/* Search results — tenders */}
        {searchResults.tenders.length > 0 && (
          <CommandGroup heading="Przetargi (live search)">
            {searchResults.tenders.map((t) => (
              <CommandItem
                key={'tender-' + t.id}
                value={'tender-' + t.id + ' ' + (t.title || '')}
                onSelect={() => handleSelect(() => navigate(`/tenders/${encodeURIComponent(t.id)}`))}
              >
                <FileText className="mr-2 h-4 w-4 text-indigo-500" />
                <div className="flex-1 min-w-0">
                  <div className="truncate text-sm">{t.title || '(bez tytułu)'}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {t.source ? t.source.toUpperCase() : ''}{t.deadline ? ` · do ${new Date(t.deadline).toLocaleDateString('pl-PL')}` : ''}
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Search results — investments */}
        {searchResults.investments.length > 0 && (
          <CommandGroup heading="Inwestycje (GUNB live)">
            {searchResults.investments.map((inv) => (
              <CommandItem
                key={'inv-' + inv.id}
                value={'inv-' + inv.id + ' ' + (inv.title || '')}
                onSelect={() => handleSelect(() => navigate(`/investments/${encodeURIComponent(inv.id)}`))}
              >
                <Building2 className="mr-2 h-4 w-4 text-emerald-500" />
                <div className="flex-1 min-w-0">
                  <div className="truncate text-sm">{inv.title || inv.investment_name || '(bez nazwy)'}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {inv.city || ''}{inv.region ? `, ${inv.region}` : ''}
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {(searchResults.tenders.length > 0 || searchResults.investments.length > 0) && (
          <CommandSeparator />
        )}

        {/* Nawigacja */}
        <CommandGroup heading="Nawigacja">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <CommandItem
                key={item.key}
                value={`nav ${item.label} ${item.tag || ''}`}
                onSelect={() => handleSelect(() => navigate(item.path))}
              >
                <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                <span className="flex-1">{item.label}</span>
                {item.tag && (
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                    {item.tag}
                  </span>
                )}
              </CommandItem>
            );
          })}
        </CommandGroup>

        <CommandSeparator />

        {/* Akcje */}
        <CommandGroup heading="Akcje">
          {actions.map((a) => {
            const Icon = a.icon;
            return (
              <CommandItem
                key={a.key}
                value={`action ${a.label}`}
                onSelect={() => handleSelect(a.onSelect)}
              >
                <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                <div className="flex-1">
                  <div>{a.label}</div>
                  {a.hint && (
                    <div className="text-[11px] text-muted-foreground">{a.hint}</div>
                  )}
                </div>
              </CommandItem>
            );
          })}
        </CommandGroup>

        <CommandSeparator />
        <div className="px-2 py-2 text-[11px] text-muted-foreground flex items-center justify-between">
          <span>Ctrl+K aby otworzyć · ↑↓ nawigacja · Enter wybór · Esc zamknij</span>
          <span className="hidden sm:inline">MASKIL Command Palette</span>
        </div>
      </CommandList>
    </CommandDialog>
  );
}
